import { ReactElement, useMemo, useState } from 'react';
import { HiOutlinePencilSquare } from 'react-icons/hi2';
import Button from '../../components/Button';
import { useNavigate } from 'react-router-dom';
import { numberToLetters } from '../../utils/letterNumber';
import { isSpecialConfig } from '../../utils/configRole';
import { configUsesGpsSpeed, gpsCircumferenceTitle, GPS_CIRCUMFERENCE_LABEL } from '../../utils/gpsSpeed';
import {
  CalibrationSeverity,
  CP_CORR_DEFAULT,
  YAW_OFFSET_DEFAULT,
  getCpCorrSeverity,
  getYawOffsetSeverity,
  isDefault,
} from '../SessionView/SessionDetails/SessionConfigs/calibrationSeverity';

type ColumnDef = {
  label: string;
  field: string;
  precision: number;
};

// Calibration severity indicator colors. Thresholds/classifiers are the shared
// single source of truth in calibrationSeverity.ts; here we only map the
// resulting severity to a dot colour and treat default values as gray.
const SEVERITY_DOT_COLORS: Record<CalibrationSeverity, string> = {
  [CalibrationSeverity.GRAY]: '#9ca3af',   // gray-400
  [CalibrationSeverity.GREEN]: '#22c55e',  // green-500
  [CalibrationSeverity.YELLOW]: '#eab308', // yellow-500
  [CalibrationSeverity.RED]: '#ef4444',    // red-500
};

function getValueSeverity(field: string, value: number | null | undefined): CalibrationSeverity | null {
  if (value == null) return null;
  if (field === 'cp_corr') {
    return isDefault(value, CP_CORR_DEFAULT) ? CalibrationSeverity.GRAY : getCpCorrSeverity(value);
  }
  if (field === 'yaw_offset') {
    return isDefault(value, YAW_OFFSET_DEFAULT) ? CalibrationSeverity.GRAY : getYawOffsetSeverity(value);
  }
  return null;
}

// Core parameters that are always displayed
const CORE_COLUMNS: ColumnDef[] = [
  { label: 'Mass', field: 'combined_mass', precision: 1 },
  { label: 'Crr', field: 'crr', precision: 5 },
  { label: 'Cp Corr', field: 'cp_corr', precision: 3 },
  { label: 'Wheel Circumference', field: 'wheel_circumference', precision: 3 },
  { label: 'Yaw Offset', field: 'yaw_offset', precision: 1 },
];

// Shown only for tyre reference/comparison sessions
const TYRE_COLUMNS: ColumnDef[] = [
  { label: 'CdA Ref', field: 'cda_ref', precision: 4 },
];

// Additional columns that may be shown for branch configs
const ADDITIONAL_COLUMNS: ColumnDef[] = [
  { label: 'Elevation Difference', field: 'elevation_difference', precision: 1 },
  { label: 'Power Slope', field: 'power_meter_slope', precision: 3 },
  { label: 'CDA Ref', field: 'cda_ref', precision: 3 },
  { label: 'Speed Ref', field: 'airspeed_ref', precision: 1 },
  { label: 'Speed Slope', field: 'airspeed_slope', precision: 5 },
  { label: 'Crosswind Slope', field: 'xw_para_slope', precision: 7 },
  { label: 'Chest Ref', field: 'chest_ref', precision: 1 },
  { label: 'Head Ref', field: 'head_ref', precision: 1 },
  { label: 'Chest Slope', field: 'chest_slope', precision: 5 },
  { label: 'Head Slope', field: 'head_slope', precision: 5 },
  { label: 'XW Wheel Compensation', field: 'xw_wheel_compensation', precision: 3 },
];

export default function CalibrationListGrid(props: {
  calibrations: any[];
  allConfigs?: any[];
  sessionId?: string;
  branchParameters?: string[];
  isTyreSession?: boolean;
}): ReactElement {
  const {
    calibrations, allConfigs = [], sessionId,
    branchParameters = [], isTyreSession = false,
  } = props;
  const navigate = useNavigate();

  const runRowStyle = 'cr-border-solid cr-border-gray-300 cr-border-b-2';
  const editButtonStyle =
    'cr-border-none cr-rounded-full cr-text-blue-900 !cr-bg-gray-100 hover:cr-bg-gray-200 cr-transition-colors cr-duration-200';

  // Determine which columns to show
  const columnsToShow = useMemo(() => {
    // Core columns are always shown regardless of data
    const columns: ColumnDef[] = [...CORE_COLUMNS];

    // Add CdA Ref for tyre sessions
    if (isTyreSession) {
      columns.push(...TYRE_COLUMNS);
    }

    // Add additional columns that are branch parameters and have data
    const additionalColumnsToShow = ADDITIONAL_COLUMNS.filter((col) => {
      // Skip if already included (e.g. cda_ref via TYRE_COLUMNS)
      if (columns.some((c) => c.field === col.field)) return false;
      if (!branchParameters.includes(col.field)) return false;
      return calibrations.some(
        (calibration) => Object.prototype.hasOwnProperty.call(calibration, col.field) && calibration[col.field] != null,
      );
    });

    return [...columns, ...additionalColumnsToShow];
  }, [calibrations, branchParameters, isTyreSession]);

  // Helper function to get total number of visible columns (including calibration and edit button)
  const getVisibleColumnCount = () => {
    return columnsToShow.length + 2; // +2 for calibration and edit button
  };

  // Helper function to generate grid template columns based on selected columns
  const getGridTemplateColumns = () => {
    const length = columnsToShow.length;
    const gridColumns = ['0.2fr', '0.5fr']; // Edit button and Calibration columns
    columnsToShow.forEach(() => {
      gridColumns.push(`${6 / length}fr`);
    });
    return gridColumns.join(' ');
  };

  // Helper function to format calibration value
  const formatValue = (calibration: any, field: string, precision: number) => {
    const value = calibration[field];
    return value != null ? value.toFixed(precision) : '-';
  };

  const handleEditClick = (calibrationId: string) => {
    if (sessionId) {
      navigate(`/sessions/${sessionId}/calibrations/${calibrationId}`);
    } else {
      navigate(`/calibrations/${calibrationId}`);
    }
  };

  // Configs that exist but don't have calibrations (branch != true)
  const uncalibratedConfigs = useMemo(() => {
    const calibratedIds = new Set(
      calibrations.map((c) => c.id),
    );
    return allConfigs
      .filter((c: any) =>
        c.letter_number !== undefined
        && !isSpecialConfig(c)
        && !calibratedIds.has(c.id))
      .sort(
        (a: any, b: any) => a.letter_number - b.letter_number,
      );
  }, [allConfigs, calibrations]);

  const [selectedConfigId, setSelectedConfigId] =
    useState<string>('');

  const handleAddCalibration = () => {
    if (!selectedConfigId) return;
    handleEditClick(selectedConfigId);
  };

  return (
    <div className="cr-w-full cr-max-w-[100vw] cr-overflow-x-auto">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: getGridTemplateColumns(),
          width: '100%',
          minWidth: 'min-content',
        }}
        className="cr-bg-gray-50 cr-px-6 cr-pb-6 cr-text-center"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: `span ${getVisibleColumnCount()}`,
          }}
          className="cr-font-secondary cr-text-base cr-uppercase"
        >
          <div className={`${runRowStyle}`}></div>
          <div
            className={`${runRowStyle} cr-flex cr-items-center cr-justify-center cr-truncate 
                        cr-text-2xs md:cr-text-sm`}
          >
            Config
          </div>
          {columnsToShow.map((col) => (
            <div
              key={col.field}
              className={`${runRowStyle} cr-flex cr-h-[100px] 
                            cr-items-center cr-justify-center md:cr-h-auto md:cr-p-2`}
            >
              <div className="cr-flex cr-h-[100px] cr-w-[10px] md:cr-h-auto md:cr-w-auto">
                <div
                  className="cr-rotate-90 cr-whitespace-nowrap cr-text-2xs md:cr-rotate-0 md:cr-transform 
              md:cr-whitespace-normal md:cr-text-sm"
                >
                  {col.label}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: `span ${getVisibleColumnCount()}`,
          }}
        >
          {calibrations &&
            calibrations.map((calibration: any) => (
              <div
                key={calibration.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'subgrid',
                  gridColumn: `span ${getVisibleColumnCount()}`,
                }}
                className="cr-font-main"
              >
                <div className={`${runRowStyle} cr-flex cr-items-center cr-justify-center`}>
                  <Button className={editButtonStyle} onClick={() => handleEditClick(calibration.id)}>
                    <HiOutlinePencilSquare className="cr-size-5" />
                  </Button>
                </div>
                <div className={`${runRowStyle} cr-flex cr-items-center cr-justify-center
                  cr-break-word cr-text-2xs md:cr-text-sm`}>
                  {(() => {
                    const definingConfig = calibration.letter;
                    if (!definingConfig) {
                      return <span className="cr-text-gray-500">{calibration.description}</span>;
                    }
                    return (
                      <span
                        className="cr-rounded cr-border-2 cr-border-solid cr-border-gray-400 cr-bg-gray-100 cr-px-1.5 cr-py-0.5 cr-text-xs cr-font-bold cr-text-blue-900"
                        title={`Defining Config ${definingConfig}`}
                      >
                        {definingConfig}
                      </span>
                    );
                  })()}
                </div>
                {columnsToShow.map((col) => {
                  const value = calibration[col.field];
                  const severity = getValueSeverity(col.field, value);
                  const hasValue = Object.prototype.hasOwnProperty.call(calibration, col.field);
                  const showGps = hasValue && col.field === 'wheel_circumference' && configUsesGpsSpeed(calibration);
                  return (
                    <div key={col.field} className={`${runRowStyle} cr-flex cr-items-center cr-justify-center cr-gap-1
                      cr-truncate cr-text-2xs md:cr-text-sm`}
                      title={showGps ? gpsCircumferenceTitle(value, col.precision) : undefined}>
                      {showGps
                        ? GPS_CIRCUMFERENCE_LABEL
                        : hasValue ? formatValue(calibration, col.field, col.precision) : '-'}
                      {severity && (
                        <span
                          style={{ backgroundColor: SEVERITY_DOT_COLORS[severity], width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }}
                          title={severity === CalibrationSeverity.GRAY ? 'Default value'
                            : severity === CalibrationSeverity.GREEN ? 'Normal range'
                              : severity === CalibrationSeverity.YELLOW ? 'Marginal range' : 'Out of range'}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>

      {/* Add Calibration for uncalibrated configs */}
      {sessionId && uncalibratedConfigs.length > 0 && (
        <div className="cr-flex cr-items-center cr-gap-3 cr-px-6 cr-py-3">
          <Button
            onClick={handleAddCalibration}
            disabled={!selectedConfigId}
          >
            Add Calibration
          </Button>
          <select
            value={selectedConfigId}
            onChange={(e) => setSelectedConfigId(e.target.value)}
            className="cr-rounded-md cr-border cr-border-gray-300
              cr-px-3 cr-py-2 cr-text-sm cr-text-gray-700"
          >
            <option value="">Select config…</option>
            {uncalibratedConfigs.map((c: any) => (
              <option key={c.id} value={c.id}>
                {numberToLetters(c.letter_number)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
